<script lang="ts" setup>
import type { Component } from 'vue';
import { computed, ref, toRefs, watch, watchEffect } from 'vue';
import { useDisplay, useTheme } from 'vuetify';
import { storeToRefs } from 'pinia';
import { configHelper, useConfigHelper } from '@ilteducation/config';
import { getShareTarget, reportShareListenStarted } from '@ilteducation/client-analytics';
import AppWrapper from './AppWrapper.vue';
import Reader from './views/Reader.vue';
import AudioOnly from './views/AudioOnly.vue';
import { breakpointThresholds } from './plugins/vuetify';
// Import CSS for epub.js audio highlights
import './assets/audio-highlights.css';
import useSettingStore from './stores/setting';
import useUiStore from './stores/ui';
import useReaderStore from './stores/reader';
import useMediaStore from './stores/media';
import useRenditionStore from './stores/rendition';
import useSmilStore from './stores/smil';
import useNavigationStore from './stores/navigation';
import useNavigationTriggerStore from './stores/navigation-trigger';
import { contentSessionStarted, setIsReadSessionEnabled } from './util/reading-session';
import {
  AdditionalStylesheets,
  Api,
  AppContext,
  BookInformation,
  BookViewMode,
  Features,
  NAVIGATION_ITEMS,
  SwitchBookPlayerModeEvent,
  TextLanguageSelectedEvent,
  UserInformation,
} from './types';
import usePlaylistStore from './stores/playlist';
import { useContentSessionEnd } from './composables/use-content-session-end';

export type Props = {
  theme: string;
  bookInformation: BookInformation;
  source?: string | File | ArrayBuffer;
  isNative: boolean;
  viewMode: BookViewMode;
  selectedLanguage?: string;
  stylesheets?: AdditionalStylesheets[];
  feature: Features;
  api: Api;
  showTooltips?: boolean;
  loginUrl: string;
  sourceApplication: string;
  userInformation: UserInformation;
  appContext?: AppContext;
  environment?: string;
  textLanguages?: string[];
  alwaysShowControls?: boolean;
  alwaysShowSettings?: boolean;
  dyslexicFont?: boolean;
  fontSize?: number;
  onBeforeRequest?: (url: string) => Promise<void>;
  rightFrameOpen?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  theme: 'default',
  showTooltips: true,
  bookInformation: (): BookInformation => ({
    contentType: 'Book',
    id: 'unknown',
    properties: {
      audioTracking: true,
      pictureInPicture: [],
      frog: false,
    },
    languages: {
      mainLanguage: 'en',
      text: [],
      audio: [],
      video: [],
    },
  }),
  source: undefined,
  isNative: false,
  viewMode: 'paginated',
  selectedLanguage: undefined,
  stylesheets: () => [],

  feature: (): Features => ({
    topBar: { show: true },
    navigation: { show: true, location: 'toolbar' },
    annotation: { show: false, location: 'toolbar' },
    contrastControls: { show: false },
    opaqueMenus: false,
    lockScreen: { show: false },
    goToStart: { show: false },
    dyslexicFontAvailable: false,
    pageTurnAnimationEnabled: false,
    touchControlsEnabled: false,
    showLastPosition: false,
    audioNarration: { show: true },
    audioTracking: { show: false },
    automaticPageTurn: { show: false },
    narrationSpeedChange: { show: false },
  }),
  api: (): Api => ({}),
  sourceApplication: 'unknown',
  environment: undefined,
  userInformation: (): UserInformation => ({
    userIdentity: {
      type: 'User',
      id: 'unknown',
      roles: [],
      organisations: [],
    },
    subscriptionType: 'unknown',
    locale: 'en',
  }),
  appContext: undefined,
  textLanguages: () => [],
  alwaysShowControls: false,
  alwaysShowSettings: false,
  dyslexicFont: false,
  fontSize: undefined,
  rightFrameOpen: false,
});

const emit = defineEmits<{
  (e: 'selectTextLanguage', event: TextLanguageSelectedEvent): void;
  (e: 'bookError', event: string): void;
  (e: 'update:dyslexicFont', event: boolean): void;
  (e: 'update:fontSize', event: number): void;
  (e: 'createDeeplink', location: string): void;
  (e: 'close'): void;
  (e: 'switch-book-player-mode', event: SwitchBookPlayerModeEvent): void;
}>();

if (props.environment) {
  // Override the environment from the config helper
  configHelper.setEnvironment(props.environment);
  useConfigHelper().setEnvironment(props.environment);
}

const { thresholds } = useDisplay();
thresholds.value = breakpointThresholds;
const mediaStore = useMediaStore();
const { preferredLanguage } = storeToRefs(mediaStore);
const {
  resetState: mediaReset,
  setPreferredLanguage,
  setMainLanguage,
  setSecondaryLanguage,
  setAudioLanguages,
  setVideoLanguages,
  setTextLanguages,
  setPictureInPicture,
} = mediaStore;
const { resetState: renditionReset, resetFontSize } = useRenditionStore();
const { resetState: smilReset } = useSmilStore();
const {
  setAudioTrackingAvailable,
  setAudioNarrationDisabled,
  setAutoPageTurnAvailable,
  setNarrationSpeedChangeAvailable,
  setAutomaticPageTurnByDefault,
  setDeviceId,
  autoPageTurn,
  partialReset: settingReset,
} = useSettingStore();
const {
  resetState: uiReset,
  setOpaqueMenus,
  setAlwaysShowControls,
  setAlwaysShowSettings,
  setDyslexicFontAvailable,
  setThemeMode,
  setLockScreenAvailable,
  setSwitchModeAvailable,
  setShowLastPosition,
  setTopBar,
  cleanupFullscreenListeners,
  exitFullscreen,
  toggleVisibleRightFrame,
} = useUiStore();
const { dyslexicFont: settingsDyslexicFont, fontSize: settingsFontSize } = storeToRefs(useSettingStore());
const {
  resetState: readerReset,
  setServiceTheme,
  setFrogBook,
  setBookType,
  setViewMode,
  setIsNative,
  setNavigationItems,
  setAnnotationItems,
  setGoToStart,
  setBookTitle,
  setBookAuthors,
  setBookCoverUrl,
  setBookId,
  setSourceApplication,
  setFileUrl,
  setReadingSession,
} = useReaderStore();

const { isAudioOnly } = storeToRefs(useReaderStore());

const { $reset: navigationReset, setHasTableOfContent, setHasAnnotations } = useNavigationStore();
const { $reset: playlistReset } = usePlaylistStore();
const { $reset: navigationTriggerReset } = useNavigationTriggerStore();

const vueTheme = useTheme();
setThemeMode(vueTheme.global.current.value.dark ? 'dark' : 'light');

const currentLanguage = ref<string | null>();

// FYI: clearPlayer must be synchronous to avoid race conditions during initialization
const clearPlayer = () => {
  console.debug('clearPlayer');
  // Clean up event listeners before resetting
  cleanupFullscreenListeners();
  mediaReset();
  navigationReset();
  navigationTriggerReset();
  playlistReset();
  renditionReset();
  smilReset();
  uiReset();
  readerReset();
  settingReset();
};

/*
 * Important: we assume that props do not change, why we reset state and set props once and only once during setup
 *
 * Later: 😞 Props do change now, what should we keep in mind?
 * 'otherTextLanguages' changes when languages are loaded
 */
const { textLanguages } = toRefs(props);
watchEffect(() => {
  setTextLanguages(textLanguages.value || []);
});

const selectTextLanguage = (event: TextLanguageSelectedEvent) => {
  emit('selectTextLanguage', event);
};

clearPlayer(); // clear the state

// Combine audio and video languages into one array
const allLanguages = [
  ...(props.bookInformation.languages?.audio || []),
  ...(props.bookInformation.languages?.video || []),
];

// Determine the current language based on selected language or available languages
currentLanguage.value =
  // If the selected language is provided and exists in the list, use it
  props.selectedLanguage && allLanguages.includes(props.selectedLanguage)
    ? props.selectedLanguage
    : // Otherwise, use the first available language in the list, or preferred language if the list is empty
      allLanguages[0] || preferredLanguage.value;

// use config stored values if props loses its data
setMainLanguage(props.bookInformation.languages?.mainLanguage);
setSecondaryLanguage(props.bookInformation.languages?.secondaryLanguage);
setAudioLanguages(props.bookInformation.languages?.audio || []);
setVideoLanguages(props.bookInformation.languages?.video || []);
setPreferredLanguage(currentLanguage.value);
setPictureInPicture(props.bookInformation.properties?.pictureInPicture || []);
setServiceTheme(props.theme);
setHasAnnotations(props.feature?.annotation?.show || false);
setAnnotationItems(props.feature?.annotation?.annotationItems ?? [], props.feature?.annotation?.location || 'toolbar');
setHasTableOfContent(props.feature?.navigation?.show || false);
setNavigationItems(
  props.feature?.navigation?.navigationItems ?? [NAVIGATION_ITEMS.TABLE_OF_CONTENT],
  props.feature?.navigation?.location || 'toolbar',
);
setGoToStart(props.feature?.goToStart?.show || false);
setFrogBook(props.bookInformation.properties?.frog || false);
setBookType(props.bookInformation.properties?.bookType);
setIsNative(props.isNative);
setViewMode(props.viewMode);
setAudioTrackingAvailable(
  !!props.bookInformation.properties?.audioTracking && (props.feature?.audioTracking?.show ?? true),
);
setAudioNarrationDisabled(!(props.feature?.audioNarration?.show ?? true));
setAutoPageTurnAvailable(props.feature?.automaticPageTurn?.show ?? true);
setNarrationSpeedChangeAvailable(props.feature?.narrationSpeedChange?.show ?? true);
setOpaqueMenus(props.feature?.opaqueMenus || false);
setAlwaysShowControls(props.alwaysShowControls);
setTopBar({ show: true, transparent: false, ...props.feature?.topBar });
setAlwaysShowSettings(props.alwaysShowSettings);
setDyslexicFontAvailable(props.feature?.dyslexicFontAvailable || false);
settingsDyslexicFont.value = props.dyslexicFont || false;
if (props.fontSize !== undefined) {
  settingsFontSize.value = props.fontSize;
}

const autoPageTurnValue = props.feature.automaticPageTurn?.forceDefault
  ? (props.feature.automaticPageTurn?.valueByDefault ?? true)
  : autoPageTurn !== undefined
    ? autoPageTurn
    : (props.feature.automaticPageTurn?.valueByDefault ?? true);

setAutomaticPageTurnByDefault(autoPageTurnValue);

setLockScreenAvailable(props.feature?.lockScreen?.show || false);
setSwitchModeAvailable(props.feature?.switchMode?.show ?? true);
setShowLastPosition(props.feature?.showLastPosition || false);

//Setup needed metadata for MediaSession API
setBookAuthors(props.bookInformation.authors);
setBookTitle(props.bookInformation.title);
setBookCoverUrl(props.bookInformation.coverUrl);

const bookId = props.bookInformation.id;

const locationWithQuery = new URL(window.location.href);
const fileUrl = (locationWithQuery.searchParams.get('fileUrl') || props.source) as string;
const startPosition = locationWithQuery.searchParams.get('position') as string;
const startHeading = decodeURIComponent(locationWithQuery.searchParams.get('heading') as string);

setIsReadSessionEnabled(true);

// init flow
setDeviceId();
setSourceApplication(props.sourceApplication);

setBookId(bookId);
if (typeof props.source === 'string') {
  setFileUrl(props.source);
}

// Read before reportShareListenStarted, which clears a signed-in reader's target. A guest's is stamped again by shared-content.
const shareTarget = getShareTarget();
const sharedContent =
  shareTarget?.contentId === bookId ? { shareId: shareTarget.shareId, openId: shareTarget.openId } : undefined;

// Start content session at BookPlayer level
const sessionId = contentSessionStarted(
  props.api,
  props.sourceApplication,
  bookId,
  props.bookInformation.contentType,
  props.userInformation,
  props.appContext,
  sharedContent,
);
setReadingSession(sessionId); // use same session-id for old readlogs so we can correlate
// Outside a share this is a no-op. Inside one it reports the start of listening with this reading
// session, which is the only exact join between the open and the player events.
reportShareListenStarted(sessionId, bookId);

toggleVisibleRightFrame(props.rightFrameOpen); //To avoid closing drawer and controls when a right frame is open.

const { endContentSession } = useContentSessionEnd();

const handlePlayerClose = async () => {
  await exitFullscreen();
  cleanupFullscreenListeners();
  emit('close');
};

const handleLogin = () => {
  endContentSession();
  window.location.href = props.loginUrl || '';
};

watch(settingsDyslexicFont, (value: boolean) => {
  emit('update:dyslexicFont', value);
});
watch(settingsFontSize, (value: number) => {
  emit('update:fontSize', value);
});
watch(
  () => props.fontSize,
  () => {
    if (props.fontSize !== undefined) {
      settingsFontSize.value = props.fontSize;
      resetFontSize();
    }
  },
);
watch(
  () => props.theme,
  (value: string) => {
    setServiceTheme(value);
  },
);

const readerComponent = computed<Component>(() => (isAudioOnly.value ? AudioOnly : Reader));
watch(
  () => props.rightFrameOpen,
  (value: boolean) => {
    toggleVisibleRightFrame(value);
  },
);

const switchBookPlayerMode = (event: SwitchBookPlayerModeEvent) => {
  emit('switch-book-player-mode', event);
};
</script>

<template>
  <AppWrapper v-bind="{ ...$props, ...$attrs }">
    <component
      :is="readerComponent"
      :user-information="props.userInformation"
      :book-id="bookId"
      :source="fileUrl"
      :stylesheets="props.stylesheets"
      :start-position="startPosition"
      :start-heading="startHeading"
      :show-tooltips="showTooltips"
      :page-turn-animation-enabled="!!feature.pageTurnAnimationEnabled"
      :page-slide-animation-enabled="!!feature.pageSlideAnimationEnabled"
      :touch-controls-enabled="!!feature.touchControlsEnabled"
      :on-before-request="props.onBeforeRequest"
      @select-text-language="selectTextLanguage"
      @close="handlePlayerClose"
      @go-to-login="handleLogin"
      @book-error="emit('bookError', $event)"
      @createDeeplink="emit('createDeeplink', $event)"
      @switch-book-player-mode="switchBookPlayerMode"
    >
      <template #toolbar-actions="slotProps">
        <slot name="toolbar-actions" v-bind="slotProps" />
      </template>
      <template #controls-actions="slotProps">
        <slot name="controls-actions" v-bind="slotProps" />
      </template>
      <template #close-btn="{ closePlayer, displayNone }">
        <slot name="close-btn" :close-player="closePlayer" :display-none="displayNone" />
      </template>
      <template #rightFrame="{ changePage, gotoPage }">
        <slot name="rightFrame" :change-page="changePage" :goto-page="gotoPage" />
      </template>
      <template #dialog="{ changePage, gotoPage }">
        <slot name="dialog" :change-page="changePage" :goto-page="gotoPage" />
      </template>
      <template #customSettings>
        <slot name="customSettings" />
      </template>
      <template #highlightMessaging="{ highlightedText, state }">
        <slot name="highlightMessaging" :highlightedText="highlightedText" :state="state" />
      </template>
    </component>
  </AppWrapper>
</template>
